import { Injectable } from '@angular/core';
import { BaseResolver } from '@app/app.resolver';
import { ModelFactory } from '@app/models/core/model-factory';
import { ApplicantIndividualMessage } from '@app/models/recruiting/applicant-individual-message.model';
import { ApplicantInterviews } from '@app/models/recruiting/applicant-interviews.model';
import { FunnelTemplate } from '@app/models/recruiting/funnel-template.model';
import { Interview } from '@app/models/recruiting/interview.model';
import { ApplicantMessageTemplate } from '@models/recruiting/applicant-message-template.model';
import { Applicant } from '@models/recruiting/applicant.model';
import { FunnelMessageTemplate } from '@models/recruiting/funnel-message-template.model';
import { FunnelTemplateStage } from '@models/recruiting/funnel-template-stages.model';
import { JobFunnelMessageTemplate } from '@models/recruiting/job-funnel-message-template.model';
import { JobFunnel } from '@models/recruiting/job-funnel.model';
import { JobPosting } from '@models/recruiting/job-posting.model';

@Injectable()
export class ApplicantTrackerResolver extends BaseResolver {
    protected modelFactory: ModelFactory = new ModelFactory([
        {
            className: 'jobPosting',
            production: {
                find: (id: number) => {
                    return JobPosting.param('company', this.companyId)
                        .with([
                            'autoArchiveQuestions',
                            'additionalQuestions',
                            'applicantOptionalFields',
                            'indeedIntegration',
                            'jobAttachments',
                            'jobIntegrations.integration.integrationType',
                            'topic',
                            'managerAssignments.employee',
                            'hiringTeam',
                            'hiringTeam.account',
                            'jobPostingLocationType',
                        ])
                        .find(id);
                },
            },
        },
        {
            className: 'applicant',
            production: {
                find: (id: number) => {
                    return Applicant.param('company', this.companyId)
                        .with([
                            'offerLetter',
                            'connectEmployeeLink',
                            'jobPosting.hiringTeam',
                            'jobPosting.managerAssignments.employee',
                            'jobPosting.jobFunnels',
                            'jobFunnel',
                            'resume',
                            'letter',
                            'answers.questionWithTrashed',
                        ])
                        .find(id);
                },
            },
        },
        {
            className: 'applicantMessageTemplate',
            production: {
                find: (id: number) => {
                    return ApplicantMessageTemplate.find(id);
                },
            },
        },
        {
            className: 'funnelTemplate',
            production: {
                find: (id: number) => {
                    return FunnelTemplate.find(id);
                },
            },
        },
        {
            className: 'email',
            production: {
                find: (id: number) => {
                    return ApplicantIndividualMessage.with('sender').find(id);
                },
            },
        },
        {
            className: 'jobFunnel',
            production: {
                find: (id: number) => {
                    return JobFunnel.find(id);
                },
            },
        },
        {
            className: 'jobFunnelMessageTemplate',
            production: {
                find: (id: number) => {
                    return JobFunnelMessageTemplate.find(id);
                },
            },
        },
        {
            className: 'funnelTemplateStage',
            production: {
                find: (id: number) => {
                    return FunnelTemplateStage.find(id);
                },
            },
        },
        {
            className: 'funnelMessageTemplate',
            production: {
                find: (id: number) => {
                    return FunnelMessageTemplate.find(id);
                },
            },
        },
        {
            className: 'interview',
            production: {
                find: (id: number): Promise<Interview> => {
                    return Interview.param('company', this.companyId)
                        .with(['interviewQuestions.interviewQuestionType', 'jobPosting', 'updatedBy'])
                        .find(id);
                },
            },
        },
        {
            className: 'applicantInterview',
            production: {
                find: (id: number): Promise<ApplicantInterviews> => {
                    return ApplicantInterviews.param('company', this.companyId)
                        .with([
                            'interview',
                            'applicant',
                            'applicant.jobPosting.hiringTeam',
                            'applicantInterviewer',
                            'applicantInterviewer.interviewer',
                            'applicantInterviewer.applicantInterviewerFeedbacks',
                            'applicantInterviewer.applicantInterviewerFeedbacks.interviewQuestion',
                            'applicantInterviewer.applicantInterviewerFeedbacks.interviewQuestion.interviewQuestionType',
                        ])
                        .find(id);
                },
            },
        },
    ]);
}
